import { useCallback, useEffect, useRef, useState } from "react";
import {
  advisoryApi,
  isUnavailable,
  type Goal,
  type Json,
  type Prefill,
  type SessionState,
  type StatusStep,
  type StepAddr,
  type StepLoad,
} from "../advisoryApi";

/** Everything a step renderer gets from the workbench. */
export interface StepProps {
  sessionId: number;
  /** The status rows this renderer covers on the current page (≥ 1). */
  steps: StatusStep[];
  addr: StepAddr;
  readOnly: boolean;
  goals: Goal[];
  currency: string;
  /** Called with the fresh state after every successful write. */
  onSaved: (state: SessionState, opts?: { goalsChanged?: boolean }) => void;
  /** Andersen / previous-answer proposal for this step, if the advisor asked for one. */
  prefill: Prefill | null;
  clearPrefill: () => void;
  /** Jump to another navigator item (e.g. from Portfolio to Custom portfolio). */
  goTo: (componentType: string) => void;
}

export interface LoadState<T> {
  data: T | null;
  error: string | null;
  unavailable: boolean;
  loading: boolean;
  reload: () => void;
}

/** GET /steps/:key for this address; re-runs when the address changes. */
export function useStepLoad<T = Json>(sessionId: number, key: string, addr: StepAddr): LoadState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const addrKey = `${addr.pageId ?? ""}|${addr.goalId ?? ""}|${addr.portfolioId ?? ""}`;

  useEffect(() => {
    let stale = false;
    setLoading(true);
    advisoryApi
      .loadStep<T>(sessionId, key, addr)
      .then((d: StepLoad<T>) => {
        if (stale) return;
        setData(d.data);
        setError(null);
        setUnavailable(false);
      })
      .catch((e) => {
        if (stale) return;
        setError((e as Error).message);
        setUnavailable(isUnavailable(e));
      })
      .finally(() => !stale && setLoading(false));
    return () => {
      stale = true;
    };
    // addr is captured through addrKey on purpose (object identity changes every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, key, addrKey, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, unavailable, loading, reload };
}

/** Save wrapper: busy flag, error text, and the workbench callback. */
export function useSaver(onSaved: StepProps["onSaved"]) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    // Set on every mount: StrictMode runs effects twice (mount, unmount, mount).
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async <R extends { state: SessionState }>(
      op: () => Promise<R>,
      opts?: { goalsChanged?: boolean },
    ): Promise<R | null> => {
      setSaving(true);
      setError(null);
      try {
        const r = await op();
        onSaved(r.state, opts);
        return r;
      } catch (e) {
        if (mounted.current) {
          setError(
            isUnavailable(e)
              ? "The advice engine is not reachable right now — your changes are still here, try saving again in a moment."
              : (e as Error).message,
          );
        }
        return null;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [onSaved],
  );

  return { saving, error, setError, run };
}

/** A component step's status row by componentType, if present on this page. */
export function stepOf(steps: StatusStep[], componentType: string): StatusStep | undefined {
  return steps.find((s) => s.componentType === componentType);
}

/** "YYYY-MM" for <input type="month"> from a QP date string. */
export function toMonth(v: string | null | undefined): string {
  return v ? v.slice(0, 7) : "";
}

/** First day of the month in the date form QP stores. */
export function fromMonth(v: string): string | null {
  return /^\d{4}-\d{2}$/.test(v) ? `${v}-01` : null;
}
